import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getGoogleAuthUrl, authenticateWithGoogle, GoogleAuthRequest } from '../services/api';
import { storeToken, storeUserData } from '@core/index';

/**
 * Hook for handling Google OAuth URL generation
 * Redirects user to Google OAuth page on success
 */
export const useGoogleAuthUrl = () => {
  return useMutation({
    mutationFn: (isLogin: boolean = true) => getGoogleAuthUrl(isLogin),
    onSuccess: (data) => {
      // Redirect to Google OAuth URL
      window.location.href = data.data.url;
    },
    onError: (error) => {
      console.error('Failed to get Google auth URL:', error);
      // Error notification is already shown by handleApiResponse
    },
  });
};

/**
 * Hook for handling Google OAuth callback authentication
 * Stores token and redirects to dashboard on success
 */
export const useGoogleAuth = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (authData: GoogleAuthRequest) => authenticateWithGoogle(authData),
    onSuccess: (data) => {
      // Store the authentication token
      storeToken(data.data.token);

      // Store complete user data with encryption
      storeUserData({
        user: data.data.user,
        games: data.data.games,
        isLoggedInFirstTime: data.data.isLoggedInFirstTime,
      });

      // Redirect to dashboard
      navigate('/', { replace: true });
    },
    onError: (error) => {
      console.error('Google authentication failed:', error);
      // Error notification is already shown by handleApiResponse
      // No need to navigate - user is already on login page
      // GoogleAuth component will handle the error UI state
    },
  });
};
